import { useEffect, useRef, useState } from 'react';
import styles from './genesys-help.component.module.less';
import classNames from 'classnames';
import HelpFallback from './help-fallback.component';

interface HelpProps {
  path?: string;
}

const defaultPath = '/help';

const Help: React.FC<HelpProps> = ({ path = defaultPath }) => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const [genesysLoaded, setGenesysLoaded] = useState(false);
  const genesysRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!scriptRef.current) {
      const script = document.createElement('script');
      script.src =
        'https://d16cchm1ejikvm.cloudfront.net/SupportCenter/CustomKP.iife.js';
      script.type = 'text/javascript';
      script.id = 'genesys-script';
      scriptRef.current = script;
      document.head.appendChild(script);

      script.onload = () => {
        console.log('Genesys script loaded successfully');

        (window as any).CustomKP &&
          (window as any).CustomKP.init({
            deploymentId: '491c4e1f-41a7-4277-b10a-48ede3ca3932',
            pathAddToDomain: `${path}`,
          });
        setGenesysLoaded(true);

        setTimeout(() => {
          setIsVisible(true);
        }, 2000);
      };

      return () => {
        document.head.removeChild(script); // Cleanup when component unmounts
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const titleElement = document.querySelector('title');
    if (!titleElement) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          refactorComponents();
        }
      }
    });

    observer.observe(titleElement, {
      subtree: true,
      characterData: true,
      childList: true,
    });

    refactorComponents();

    return () => observer.disconnect();
  }, []);

  const addClassToElement = (element: HTMLElement, className: string) => {
    if (element && !element.classList.contains(className)) {
      element.classList.add(className);
    }
  };

  function waitForElement(
    container: HTMLElement,
    selector: string,
    callback: (el: Element) => void,
    options = { subtree: true, childList: true }
  ) {
    const existingElement = container.querySelector(selector) as HTMLElement;
    if (existingElement) {
      callback(existingElement);
      return;
    }
    const observer = new MutationObserver((mutationsList, observerInstance) => {
      for (const mutation of mutationsList) {
        const targetElement = document.querySelector(selector);
        if (targetElement) {
          callback(targetElement);
          observerInstance.disconnect(); // Stop observing
          return;
        }
      }
    });
    observer.observe(document.body, options);

    return () => {
      observer.disconnect();
    };
  }

  const addClassToBodyTag = (className: string) => {
    const body = document.querySelector('body');
    if (body) {
      addClassToElement(body as HTMLElement, className);
    }
  };

  const updateClass = (element: HTMLElement, from: string, to: string) => {
    if (element && element.classList.contains(from)) {
      element.classList.remove(from);
      element.classList.add(to);
    }
  };

  const increaseContainerWidth = (element: HTMLElement) => {
    updateClass(element, 'lg-col-8', 'lg-col-10');
    updateClass(element, 'lg-offset-2', 'lg-offset-1');
  };

  const updateContainerWidths = () => {
    waitForElement(document.body, '#mainContainer', (mainContainer) => {
      const gcRows = Array.from(
        mainContainer.querySelectorAll('.gc-row')
      ) as HTMLElement[];
      const filteredGcRows = gcRows.filter((gcRow) => {
        return !gcRow.classList.contains('categoriesContainer');
      });

      filteredGcRows.forEach((gcRow) => {
        const layoutContainer = gcRow.children[0] as HTMLElement;
        if (!layoutContainer) {
          return;
        }

        increaseContainerWidth(layoutContainer);
      });
    });
  };

  const removeNumber = (element: HTMLElement) => {
    if (!element || !element.textContent) return;
    const textContent = element.textContent;
    if (textContent && textContent.match(/^\d+\.\s*/)) {
      element.textContent = textContent.replace(/^\d+\.\s*/, '');
    }
  };

  const removeListNumbers = () => {
    waitForElement(
      document.body,
      '.categoriesContainer',
      (containerElement) => {
        if (containerElement) {
          const items = containerElement.querySelectorAll(
            'span[data-testid="category-item-title"]'
          );
          items.forEach((item) => {
            removeNumber(item as HTMLElement);
          });
        }
      }
    );
  };

  const removeTitleNumbers = () => {
    waitForElement(
      document.body,
      'div[class*="sectionHeader"]',
      (containerElement) => {
        if (containerElement) {
          const titles = containerElement.querySelectorAll('h2');
          titles.forEach((title) => {
            removeNumber(title as HTMLElement);
          });
        }
      }
    );
  };

  const removeBreadcrumbNumbers = () => {
    waitForElement(
      document.body,
      '.navigationBreadcrumb',
      (containerElement) => {
        if (containerElement) {
          const listItems = containerElement.querySelectorAll('li');
          listItems.forEach((listItem) => {
            const link = listItem.querySelector('a');
            if (link) {
              removeNumber(link as HTMLElement);
            } else {
              removeNumber(listItem as HTMLLIElement);
            }
          });
        }
      }
    );
  };

  const updatePageTitle = () => {
    const DEFAULT_TITLE = 'Home';
    const head = document.querySelector('head');
    if (head) {
      const pageTitle = head.querySelector('title');
      if (pageTitle) {
        const currentTitle = pageTitle.textContent || '';
        if (currentTitle === DEFAULT_TITLE) {
          pageTitle.textContent = 'Help Centre | Virgin Atlantic';
        }
        removeNumber(pageTitle as HTMLElement);
      }
    }
  };

  const refactorComponents = () => {
    addClassToBodyTag('genesys-support-page');
    updateContainerWidths();
    removeListNumbers();
    removeTitleNumbers();
    removeBreadcrumbNumbers();
    updatePageTitle();
  };

  useEffect(() => {
    if (genesysLoaded) {
      console.log('Genesys loaded');
      refactorComponents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genesysLoaded]);

  return (
    <div className={classNames(styles.genesysSupportCenter)}>
      {!isVisible && <HelpFallback />}
      <div
        id="genesys-support-center"
        ref={genesysRef}
        className={classNames({ [styles.hidden]: !isVisible })}
      ></div>
    </div>
  );
};

export default Help;
